import { useEffect, useId, useRef, useState } from 'react';
import { DialogShell } from '../../../components/DialogShell';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { BUILTIN_SYMBOLS, builtinSymbol, graphicName } from '../../../symbols/catalogue';
import { type SimpleShapeId, type SymbolGraphic } from '../../../symbols/model';
import { graphicKey } from '../../../symbols/tint';
import { symbolUsage } from '../../../symbols/registry';
import type { SymbolTarget } from '../../../symbols/targets';
import { useAppStore } from '../../state/useAppStore';
import { useCapability } from '../../../features/useCapability';
import { SymbolArtwork } from './SymbolResources';
import { SvgHelp } from './SvgHelp';
import type { SymbolPresentation } from '../../../symbols/backing';

export function SymbolPicker<S extends SimpleShapeId>({
  target,
  graphic,
  onChange,
  shapes,
  colour = '#334155',
  tint,
  presentation,
}: {
  readonly target: SymbolTarget;
  readonly graphic: SymbolGraphic<S>;
  readonly onChange: (graphic: SymbolGraphic<S>, defaultAnchor?: 'center' | 'bottom') => void;
  readonly shapes: readonly S[];
  readonly colour?: string;
  readonly tint?: string | null;
  readonly presentation?: SymbolPresentation;
}) {
  const project = useAppStore((s) => s.project);
  const importFile = useAppStore((s) => s.importSymbolForTarget);
  const cancel = useAppStore((s) => s.cancelSymbolImport);
  const rename = useAppStore((s) => s.renameSymbolAsset);
  const remove = useAppStore((s) => s.removeSymbolAsset);
  const busy = useAppStore((s) => s.symbolImportBusy);
  const status = useAppStore((s) => s.symbolImportMessage);
  const canImport = useCapability('symbols.import');
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpTrigger = useRef<HTMLButtonElement>(null);
  const closeHelp = () => {
    setHelpOpen(false);
    requestAnimationFrame(() => helpTrigger.current?.focus());
  };
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState<{ id: string; uses: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null);
  const replacement = useRef<{ id: string; uses: number } | null>(null);
  const targetKey = JSON.stringify(target);
  const labelId = useId();
  useEffect(() => () => cancel(), [targetKey, project.sources, cancel]);
  const selected = graphicKey(graphic);
  const choose = (value: SymbolGraphic<S>) => {
    cancel();
    onChange(
      value,
      typeof value !== 'string' && value.kind === 'builtin'
        ? builtinSymbol(value.id).defaultAnchor
        : 'center',
    );
    setNotice('');
  };
  const pickFile = (replace?: { id: string; uses: number }) => {
    replacement.current = replace ?? null;
    fileRef.current?.click();
  };
  const close = () => {
    cancel();
    setOpen(false);
    setEditing(null);
  };
  const matches = (value: string) => value.toLowerCase().includes(search.toLowerCase().trim());
  const choice = (value: SymbolGraphic<S>, name: string) => (
    <button
      className="symbol-library-choice"
      type="button"
      aria-pressed={selected === graphicKey(value)}
      onClick={() => choose(value)}
    >
      <svg className="symbol-thumbnail" viewBox="0 0 30 24" aria-hidden="true">
        <SymbolArtwork graphic={value} x={15} y={12} longEdge={20} />
      </svg>
      <span>{name}</span>
      <span className="symbol-choice-check" aria-hidden="true">
        {selected === graphicKey(value) ? '✓' : ''}
      </span>
    </button>
  );
  return (
    <>
      <div className="style-field style-field-inline">
        <span id={labelId}>Symbol</span>
        <button
          data-symbol-graphic={selected}
          className="symbol-picker-trigger"
          type="button"
          aria-labelledby={`${labelId} ${labelId}-value`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => {
            setReturnFocusTo(event.currentTarget);
            setNotice('');
            setSearch('');
            setOpen(true);
          }}
        >
          <svg viewBox="0 0 30 24" aria-hidden="true">
            <SymbolArtwork
              graphic={graphic}
              colour={colour}
              {...(tint === undefined ? {} : { tint })}
              {...(presentation === undefined ? {} : { presentation })}
              x={15}
              y={12}
              longEdge={20}
            />
          </svg>
          <span id={`${labelId}-value`}>{graphicName(graphic, project.symbolAssets)}</span>
          <span aria-hidden="true">▾</span>
        </button>
      </div>
      <input
        ref={fileRef}
        aria-label="Import symbol file"
        type="file"
        hidden
        accept=".svg,.png,image/svg+xml,image/png"
        onChange={async (e) => {
          const file = e.target.files?.[0],
            replace = replacement.current;
          e.target.value = '';
          replacement.current = null;
          if (!file) return;
          setNotice('');
          const result = await importFile(target, file, replace?.id, replace?.uses);
          if (!result.ok) setNotice(result.message);
        }}
      />
      {!open && (notice || status) ? (
        <p className="style-help" role="status">
          {notice || status}
        </p>
      ) : null}
      {!open && busy ? (
        <button type="button" onClick={cancel}>
          Cancel symbol import
        </button>
      ) : null}
      {open ? (
        <DialogShell
          ariaLabel={helpOpen ? 'SVG help' : 'Symbols'}
          className="symbol-library-dialog"
          onClose={helpOpen ? closeHelp : close}
          returnFocusTo={returnFocusTo}
          showCloseButton
        >
          {helpOpen ? (
            <SvgHelp onBack={closeHelp} />
          ) : (
            <>
              <h2>Symbols</h2>
              <input
                className="symbol-library-search"
                aria-label="Search symbols"
                type="search"
                placeholder="Search symbols"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="symbol-library-scroll">
                <h3>Shapes</h3>
                <div className="symbol-library-grid">
                  {shapes
                    .filter((s) => matches(s))
                    .map((shape) => (
                      <div key={shape}>{choice(shape, graphicName(shape, []))}</div>
                    ))}
                </div>
                <h3>Transport &amp; Planning</h3>
                <div className="symbol-library-grid">
                  {BUILTIN_SYMBOLS.filter((b) => matches(`${b.name} ${b.keywords}`)).map((b) => (
                    <div key={b.id}>{choice({ kind: 'builtin', id: b.id }, b.name)}</div>
                  ))}
                </div>
                <h3>My Symbols</h3>
                {!project.symbolAssets.length ? (
                  <p className="style-help">
                    Your imported artwork stays in this project and can be shared by layers and
                    markers.
                  </p>
                ) : null}
                <div className="symbol-library-items">
                  {project.symbolAssets
                    .filter((a) => matches(a.name))
                    .map((asset) => {
                      const uses = symbolUsage(project, asset.id).length;
                      return (
                        <div className="symbol-library-item" key={asset.id}>
                          {choice({ kind: 'asset', assetId: asset.id }, asset.name)}
                          {editing === asset.id ? (
                            <form
                              className="symbol-library-rename"
                              onSubmit={(e) => {
                                e.preventDefault();
                                const result = rename(asset.id, name);
                                setNotice(result.ok ? 'Symbol renamed.' : result.message);
                                if (result.ok) setEditing(null);
                              }}
                            >
                              <input
                                aria-label="Symbol name"
                                maxLength={120}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                              />
                              <button type="submit">Apply</button>
                              <button type="button" onClick={() => setEditing(null)}>
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <div className="symbol-library-management">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditing(asset.id);
                                  setName(asset.name);
                                }}
                              >
                                Rename
                              </button>
                              <button
                                type="button"
                                disabled={!canImport}
                                onClick={() =>
                                  uses > 1
                                    ? setConfirm({ id: asset.id, uses })
                                    : pickFile({ id: asset.id, uses })
                                }
                              >
                                Replace everywhere
                              </button>
                              <button
                                type="button"
                                disabled={uses > 0 || !canImport}
                                title={
                                  uses
                                    ? `Used by ${uses} styles. Change those references first.`
                                    : 'Remove unused symbol'
                                }
                                onClick={() => {
                                  const result = remove(asset.id);
                                  setNotice(result.ok ? 'Unused symbol removed.' : result.message);
                                }}
                              >
                                Remove
                              </button>
                              <span className="style-help">
                                {uses ? `Used by ${uses} styles` : 'Unused'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="symbol-library-footer">
                <button
                  className="secondary-button"
                  type="button"
                  disabled={!canImport || busy}
                  onClick={() => pickFile()}
                >
                  Import SVG/PNG
                </button>
                <span className="style-help">
                  Local artwork ·{' '}
                  <button
                    ref={helpTrigger}
                    className="symbol-help-trigger"
                    type="button"
                    aria-label="SVG help"
                    onClick={() => setHelpOpen(true)}
                  >
                    SVG help ⓘ
                  </button>
                </span>
                <button className="secondary-button" type="button" onClick={close}>
                  Done
                </button>
              </div>
              {notice || status ? (
                <p role="status" className="style-help">
                  {notice || status}
                </p>
              ) : null}
              {busy ? (
                <button type="button" onClick={cancel}>
                  Cancel symbol import
                </button>
              ) : null}
            </>
          )}
        </DialogShell>
      ) : null}
      {confirm ? (
        <ConfirmationDialog
          title="Replace symbol everywhere?"
          description={`The new artwork will replace this symbol in all ${confirm.uses} styles. Its name and references will remain.`}
          confirmLabel="Choose replacement"
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            pickFile(confirm);
            setConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}
